/**
 * DraftImageStore
 *
 * Stores draft image Files in IndexedDB by draftId and nodeId.
 * Persists across page reload and browser restart until explicitly deleted.
 *
 * Record schema:
 *   { key: string (draftId_nodeId), file: File, name: string, type: string, createdAt: number }
 */

import {STORAGE_KEYS} from '../constants/storageKeys'
import {IndexedDBAdapter} from './IndexedDBAdapter'

const adapter = IndexedDBAdapter({
    dbName: STORAGE_KEYS.DRAFT_DB_NAME,
    storeName: STORAGE_KEYS.DRAFT_IMAGES_STORE,
    dbVersion: STORAGE_KEYS.DRAFT_DB_VERSION,
    allStoreNames: STORAGE_KEYS.DRAFT_DB_STORES,
})

function makeKey(draftId, nodeId) {
    return `${draftId}_${nodeId}`
}

const DraftImageStore = {
    /**
     * Save a File under the given draftId and nodeId.
     * @param {string} draftId - Draft identifier (use '_current' for unsaved session)
     * @param {string} nodeId
     * @param {File} file
     * @returns {Promise<void>}
     */
    async save(draftId, nodeId, file) {
        const now = Date.now()
        await adapter.save(makeKey(draftId, nodeId), {
            file,
            name: file.name,
            type: file.type,
            createdAt: now,
        })
    },

    /**
     * Load a File record by draftId and nodeId.
     * @param {string} draftId
     * @param {string} nodeId
     * @returns {Promise<File | null>}
     */
    async load(draftId, nodeId) {
        const record = await adapter.load(makeKey(draftId, nodeId))
        return record?.file ?? null
    },

    /**
     * Delete a File record by draftId and nodeId.
     * @param {string} draftId
     * @param {string} nodeId
     * @returns {Promise<void>}
     */
    async delete(draftId, nodeId) {
        return adapter.delete(makeKey(draftId, nodeId))
    },

    /**
     * Delete all file records for a given draftId.
     * @param {string} draftId
     * @returns {Promise<void>}
     */
    async deleteByDraftId(draftId) {
        const allKeys = await adapter.getAllKeys()
        const prefix = `${draftId}_`
        const keysToDelete = allKeys.filter((k) => k.startsWith(prefix))
        await Promise.all(keysToDelete.map((k) => adapter.delete(k)))
    },

    /**
     * Get all keys that belong to a given draftId.
     * @param {string} draftId
     * @returns {Promise<string[]>} Array of full keys (draftId_nodeId)
     */
    async getKeysByDraftId(draftId) {
        const allKeys = await adapter.getAllKeys()
        const prefix = `${draftId}_`
        return allKeys.filter((k) => k.startsWith(prefix))
    },

    /**
     * Migrate images from one draftId to another.
     * Loads each file, saves under new draftId, deletes old key.
     * @param {string} fromDraftId
     * @param {string} toDraftId
     * @returns {Promise<void>}
     */
    async migrateDraftId(fromDraftId, toDraftId) {
        const keys = await this.getKeysByDraftId(fromDraftId)
        for (const key of keys) {
            const nodeId = key.slice(`${fromDraftId}_`.length)
            const record = await adapter.load(key)
            if (record?.file) {
                await this.save(toDraftId, nodeId, record.file)
                await adapter.delete(key)
            }
        }
    },

    /**
     * Clear all stored files.
     * @returns {Promise<void>}
     */
    async clear() {
        return adapter.clear()
    },
}

export default DraftImageStore
